import React, { useState } from 'react';
import { Key, Eye, EyeOff, Copy, Check, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/loading-states';
import { useTenantSettings } from '../../hooks/settings/useSettings';
import { GenerateQRSecretDialog } from './GenerateQRSecretDialog';

export const QRSecretCard: React.FC = () => {
  const { data: settings, isLoading, error } = useTenantSettings();
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);

  const handleCopySecret = async () => {
    if (settings?.qr_secret) {
      await navigator.clipboard.writeText(settings.qr_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const maskSecret = (secret: string): string => {
    if (!secret || secret.length < 8) return secret;
    const visibleChars = 6;
    const start = secret.substring(0, visibleChars);
    const masked = '*'.repeat(Math.min(secret.length - visibleChars, 24));
    return `${start}${masked}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            QR код секрет
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            QR код секрет
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              Ошибка загрузки настроек: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const secretToDisplay = settings?.qr_secret || '';
  const displaySecret = showSecret ? secretToDisplay : maskSecret(secretToDisplay);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                QR код секрет
              </CardTitle>
              <CardDescription>
                Криптографический ключ для подписи QR-кодов локаций
              </CardDescription>
            </div>
            <Badge variant="default">Активен</Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Secret display */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Секретный ключ:
            </label>
            <div className="bg-gray-100 rounded-md p-3 font-mono text-sm break-all flex items-center justify-between gap-2">
              <span className="flex-1">
                {displaySecret}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSecret(!showSecret)}
                title={showSecret ? 'Скрыть' : 'Показать'}
                className="flex-shrink-0"
              >
                {showSecret ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCopySecret}
              disabled={!secretToDisplay}
              className="flex-1"
            >
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4 text-green-600" />
                  Скопировано
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Копировать
                </>
              )}
            </Button>

            <Button
              variant="default"
              onClick={() => setIsGenerateDialogOpen(true)}
              className="flex-1"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Сгенерировать новый
            </Button>
          </div>

          {/* Info message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-900">
              <strong>Важно:</strong> Этот ключ используется для HMAC-подписи QR-кодов.
              Храните его в безопасности. При генерации нового ключа все старые QR-коды станут недействительными.
            </p>
          </div>

          {/* Last updated */}
          {settings?.updated_at && (
            <p className="text-xs text-muted-foreground">
              Последнее обновление: {new Date(settings.updated_at).toLocaleString('ru-RU')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Generate dialog */}
      <GenerateQRSecretDialog
        isOpen={isGenerateDialogOpen}
        onClose={() => setIsGenerateDialogOpen(false)}
      />
    </>
  );
};
